import React from 'react';
import blueCheckMark from '../passwordTextField/assets/blueCheckMark.svg';
import transparentSvg from '../passwordTextField/assets/transparent.svg';
import styles from './strong-password-check.module.scss';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export const hasAtLeastOneLowerCaseLetter = /(?=.*[a-z])/;
export const hasAtLeastOneNumericDigit = /(?=.*[0-9])/;
export const hasAtLeastOneSpecialCaseLetter = /(?=.*\W)/;
export const hasAtLeastOneUppercaseLetter = /(?=.*[A-Z])/;
export const hasLengthBetweenEightAndFifty = /(?=.{8,50})/;

interface StrongPasswordCheckRuleV1Props {
    message: string;
    isValid: boolean;
}

const StrongPasswordCheckRule = ({ message, isValid }: StrongPasswordCheckRuleV1Props) => {
    const { t } = useTranslation('shared');
    const src = isValid ? blueCheckMark : transparentSvg;
    const alt = isValid ? t('StrongPasswordCheckRule.checkedAlt') : t('StrongPasswordCheckRule.notCheckedAlt');

    return (
        <li className={styles.li}>
            <img className={styles.circleIcon} src={src} alt={alt} />
            <div className={classNames(styles.message, { [styles.validMessage]: isValid })}>{message}</div>
        </li>
    );
};

interface StrongPasswordCheckProps {
    password: string;
    onValidate?: () => void;
}

export const StrongPasswordCheck = ({ password }: StrongPasswordCheckProps) => {
    const { t } = useTranslation('shared');

    const rules = [
        {
            regex: hasAtLeastOneLowerCaseLetter,
            message: t('StrongPasswordCheck.lowerCaseLetters')
        },
        {
            regex: hasAtLeastOneUppercaseLetter,
            message: t('StrongPasswordCheck.upperCaseLetters')
        },
        {
            regex: hasAtLeastOneNumericDigit,
            message: t('StrongPasswordCheck.numericDigits')
        },
        {
            regex: hasAtLeastOneSpecialCaseLetter,
            message: t('StrongPasswordCheck.specialCharacters')
        },
        {
            regex: hasLengthBetweenEightAndFifty,
            message: t('StrongPasswordCheck.atLeast8Characters')
        }
    ];

    const validatedRules = rules.map(({ message, regex }) => ({
        message,
        isValid: regex.test(password)
    }));

    return (
        <div className={styles.container}>
            <strong className={styles.title}>{t('StrongPasswordCheck.includes')}</strong>
            <ul className={styles.ul}>
                {validatedRules.map((validatedRule, i) => (
                    <StrongPasswordCheckRule key={i} {...validatedRule} />
                ))}
            </ul>
        </div>
    );
};
